import { Injectable } from '@angular/core';
import { LeaveAdminDashboardData } from '@core/models';
import { LeaveAdminDashboardStore } from '@core/models/leave-admin/leave-admin-dashboard-store.model';
import { LeaveAdminDashboardTableFilters } from '@core/models/leave-admin/leave-admin-dashboard-table-filters.model';
import { LeaveListItem } from '@core/models/leave-admin/leave-list-item.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { LeaveAdminDashboardFormService } from './leave-admin-dashboard-form.service';

@Injectable({
  providedIn: 'root'
})
export class LeaveAdminDashboardStoreService {

  private readonly _initialStoreData: LeaveAdminDashboardStore = {
    leaveAdmin: null,
    sysText: null
  };

  private leaveAdminDashboardStore: BehaviorSubject<LeaveAdminDashboardStore>;  

  private store$: Observable<LeaveAdminDashboardStore>;

  private readonly templateNames = {
    leaveAdmin: "leaveAdmin",
    assignCaseAdminDialog: "assignCaseAdminDialog",
    submitNewRequest: 'submitNewRequest',
    unsavedChanges: "unsavedChanges",
    leaveAdminDashboardTableFilterDialog: "leaveAdminDashboardTableFilterDialog",
    duplicateDetected: "duplicateDetected",
    reports: "reports"
  };

  constructor(
    private formService: LeaveAdminDashboardFormService
  ) { 
    this.leaveAdminDashboardStore = new BehaviorSubject(this._initialStoreData);
    this.store$ = this.leaveAdminDashboardStore.asObservable();
  }

  public get templates() {
    return this.templateNames;
  }
  
  public get store() {
    return this.leaveAdminDashboardStore.value;
  }
  
  public get leaveAdmin$(): Observable<LeaveAdminDashboardData> {
    return this.store$.pipe(pluck('leaveAdmin'));
  }

  public get leaveAdmin() {
    return this.store.leaveAdmin;
  }

  public get sysText() {
    return this.store.sysText;
  }

  public set leaveAdmin(leaveAdmin: LeaveAdminDashboardData) {
    this.checkRowDetails(leaveAdmin.allLeaves);
    const store = { ...this.store, leaveAdmin };
    this.leaveAdminDashboardStore.next(store);
  }
  
  public set sysText(sysText: any) {
    const store = { ...this.store, sysText };
    this.leaveAdminDashboardStore.next(store);
  }

  public set tableFilterOptions(tableFilterOptions: LeaveAdminDashboardTableFilters) {
    const store = { ...this.store, tableFilterOptions };
    this.leaveAdminDashboardStore.next(store);
  }

  public updateLeaveListItem(item: LeaveListItem): void {
    const leaveAdmin = this.leaveAdmin;
    const index = leaveAdmin.allLeaves.findIndex(i => i.caseId === item.caseId);
    leaveAdmin.allLeaves.splice(index, 1, item);
    const store = { ...this.store, leaveAdmin };
    this.leaveAdminDashboardStore.next(store);
  }

  public unloadStore(): void {
    const store: LeaveAdminDashboardStore = {
      leaveAdmin: null,
      sysText: null
    }
    this.leaveAdminDashboardStore.next(store);
  }

  private checkRowDetails(newLeaveData: LeaveListItem[]): void {
    // if all details are not collapsed (length 0), check for 
    // any newly added cases and set their details to expanded
    if (this.formService.detailsControl.value?.length != 0) {
      const oldLeaveData = this.store.leaveAdmin?.allLeaves;
      const newCases = newLeaveData?.filter((nld) => oldLeaveData?.every((old) => old.caseId !== nld.caseId));
      newCases.forEach(nc => {
        if (!this.formService.detailsControl.value.includes(nc.caseId)) {
          this.formService.detailsControl.value.push(nc.caseId);
        }
      });
    }
  }
}
